import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { REPORT_YEARS, buildReport } from '../data/sampleAnnualReport';
import ReportReader from '../components/report/ReportReader';
import PdfFlipReader from '../components/report/PdfFlipReader';
import { BookOpen, Download, FileText, Sparkles } from 'lucide-react';

const parseHash = () => {
  const h = window.location.hash.replace(/^#/, '');
  const params = new URLSearchParams(h);
  return { report: params.get('report'), page: parseInt(params.get('page') || '1', 10) || 1 };
};

export default function AnnualReportsPage() {
  const [sheetYear, setSheetYear] = useState(null);
  const [reader, setReader] = useState(null); // { report, page }

  useEffect(() => {
    const { report, page } = parseHash();
    if (report) {
      const r = buildReport(report);
      if (r) setReader({ report: r, page });
    }
    window.scrollTo(0, 0);
  }, []);

  const openReader = (id, page = 1) => {
    const r = buildReport(id);
    if (r) {
      setSheetYear(null);
      setReader({ report: r, page });
    }
  };

  const closeReader = () => {
    setReader(null);
    window.history.replaceState(null, '', window.location.pathname);
  };

  const latest = REPORT_YEARS[0];
  const rest = REPORT_YEARS.slice(1);
  const sheetMeta = sheetYear ? REPORT_YEARS.find((r) => r.id === sheetYear) : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-blue-50/40 to-white">
      <Header />
      <main className="max-w-6xl mx-auto px-4 py-14 md:py-20" data-testid="annual-reports-page">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-[2px] w-10 bg-gradient-to-r from-[#1565C0] to-[#D4AF37] rounded-full" />
            <span className="text-[11px] uppercase tracking-[0.2em] text-[#1565C0] font-bold">Transparency</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-[#0A1628] font-serif">Annual <span className="text-[#1565C0]">Reports</span></h1>
          <p className="text-gray-600 mt-3 max-w-2xl text-base">
            Every year EWRF publishes an independently audited report. Read any report online or download the PDF.
          </p>
        </div>

        {/* Featured latest */}
        <button onClick={() => setSheetYear(latest.id)}
          className="w-full text-left group mb-10 focus:outline-none focus-visible:ring-2 ring-[#1565C0] rounded-3xl"
          data-testid="featured-report-card">
          <div className="relative bg-gradient-to-br from-[#0A1628] via-[#0D2444] to-[#0A1E38] rounded-3xl p-8 md:p-10 text-white overflow-hidden shadow-2xl">
            <div className="absolute -top-16 -right-10 w-64 h-64 bg-[#1565C0]/30 rounded-full blur-3xl" />
            <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-[#D4AF37]/20 rounded-full blur-3xl" />
            <div className="relative flex flex-col md:flex-row md:items-center gap-6">
              <div className="w-24 h-32 bg-white rounded-lg shadow-xl flex flex-col items-center justify-center flex-shrink-0 group-hover:scale-105 group-hover:-rotate-2 transition-transform">
                <FileText className="w-8 h-8 text-[#1565C0]" />
                <div className="text-[9px] font-black text-[#0A1628] mt-2 text-center leading-tight px-1">ANNUAL REPORT<br />{latest.label}</div>
              </div>
              <div className="min-w-0">
                <div className="inline-flex items-center gap-1.5 bg-[#D4AF37] text-[#0A1628] text-[10px] font-black uppercase tracking-widest rounded-full px-3 py-1 mb-3">
                  <Sparkles className="w-3 h-3" /> Latest report
                </div>
                <h2 className="text-lg md:text-lg font-black font-serif">Annual Report {latest.label}</h2>
                <p className="text-white/70 text-sm mt-1.5 max-w-xl">
                  Programmes, impact numbers and audited financials for the year — read it online with search and page sharing, or download the PDF ({latest.size}).
                </p>
              </div>
              <div className="md:ml-auto flex-shrink-0">
                <span className="inline-flex items-center gap-2 bg-white text-[#0A1628] font-bold rounded-full px-5 py-2.5 text-sm group-hover:bg-[#D4AF37] transition-colors">
                  <BookOpen className="w-4 h-4" /> Open
                </span>
              </div>
            </div>
          </div>
        </button>

        {/* Year grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="report-year-grid">
          {rest.map((r) => (
            <button key={r.id} onClick={() => setSheetYear(r.id)}
              className="group bg-white border-2 border-gray-100 hover:border-[#1565C0]/40 hover:shadow-xl rounded-2xl p-5 text-left transition-all focus:outline-none focus-visible:ring-2 ring-[#1565C0]"
              data-testid={`report-year-${r.id}`}>
              <div className="w-10 h-12 bg-blue-50 rounded-md flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <FileText className="w-5 h-5 text-[#1565C0]" />
              </div>
              <div className="font-black text-[#0A1628] font-mono">{r.label}</div>
              <div className="text-[11px] text-gray-500 mt-1">PDF · {r.size}</div>
            </button>
          ))}
        </div>
      </main>
      <Footer />

      {/* Year sheet */}
      <Sheet open={!!sheetMeta} onOpenChange={(o) => !o && setSheetYear(null)}>
        <SheetContent side="bottom" className="rounded-t-3xl max-w-lg mx-auto pb-10" data-testid="report-year-sheet">
          <SheetHeader className="text-left">
            <SheetTitle className="font-serif text-2xl text-[#0A1628]">Annual Report {sheetMeta?.label}</SheetTitle>
            <SheetDescription>Read online in the interactive reader, or download the official PDF.</SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-3">
            <Button onClick={() => openReader(sheetMeta.id)} className="w-full bg-[#1565C0] hover:bg-[#0D47A1] h-12 text-base" data-testid="sheet-read-online-btn">
              <BookOpen className="w-5 h-5 mr-2" /> Read online
            </Button>
            <Button asChild variant="outline" className="w-full h-12 text-base" data-testid="sheet-download-btn">
              <a href={sheetMeta?.href} target="_blank" rel="noreferrer noopener">
                <Download className="w-5 h-5 mr-2" /> Download PDF ({sheetMeta?.size})
              </a>
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {reader && (reader.report.pdf
        ? <PdfFlipReader report={reader.report} initialPage={reader.page} onClose={closeReader} />
        : <ReportReader report={reader.report} initialPage={reader.page} onClose={closeReader} />)}
    </div>
  );
}
